import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskLines } from "./Reveal";
import { IMAGES } from "../../data";
import { scrollToId } from "../../lib/scroll";
import { ArrowDown } from "lucide-react";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1500px] px-6 lg:px-12 min-h-screen flex flex-col justify-end pb-20 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-4 mb-8">
          <span className="h-px w-12 bg-gold" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-gold">Where Capital Meets Consciousness</span>
        </motion.div>

        <h1 className="font-serif font-light text-parchment tracking-tight text-[9vw] leading-[0.92] sm:text-[8vw] lg:text-[6.5rem] xl:text-[7.5rem] max-w-[16ch]">
          <MaskLines lines={["Transforming Visionary", <>Companies into <span className="italic text-gold">Enduring Enterprises.</span></>]} delay={0.5} className="block" />
        </h1>
        <p className="mt-6 font-serif italic text-2xl sm:text-3xl text-gold/90">Where Capital Meets Consciousness.</p>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-6 text-stone text-base sm:text-lg leading-relaxed max-w-xl"
          >
            EQ Capital Group is the leading impact-focused investment bank and investor-relations firm — with the deepest access to Ultra-High-Net-Worth individuals and the largest family office and private wealth network worldwide.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-6 lg:justify-self-end"
          >
            <button
              data-testid="hero-cta"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-4 font-mono text-xs tracking-[0.2em] uppercase border border-gold text-gold px-8 py-4 hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Begin a Consultation
              <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-8 right-6 lg:right-12 z-10 hidden sm:flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">Scroll</span>
        <span className="h-10 w-px bg-hairline overflow-hidden">
          <motion.span className="block h-3 w-px bg-gold" animate={{ y: [0, 28, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
        </span>
      </motion.div>
    </section>
  );
};
